import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook to check if a memorial has accepted collaborators
 * Only activates story locking when collaborators exist
 */
export const useHasCollaborators = (memorialId: string | undefined) => {
  const [hasCollaborators, setHasCollaborators] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!memorialId) {
      setHasCollaborators(false);
      setIsLoading(false);
      return;
    }

    const checkCollaborators = async () => {
      try {
        const { data, error } = await supabase
          .from('memorial_collaborators')
          .select('id')
          .eq('memorial_id', memorialId)
          .eq('status', 'accepted')
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('[useHasCollaborators] Error checking collaborators:', error);
          setHasCollaborators(false);
        } else {
          setHasCollaborators(!!data);
        }
      } catch (error) {
        console.error('[useHasCollaborators] Exception:', error);
        setHasCollaborators(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkCollaborators();

    // Subscribe to realtime changes in collaborators
    const channel = supabase
      .channel(`memorial-collaborators-${memorialId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'memorial_collaborators',
          filter: `memorial_id=eq.${memorialId}`
        },
        () => {
          checkCollaborators();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [memorialId]);

  return { hasCollaborators, isLoading };
};
